import type { FC } from 'react';
import { SurfaceRenderer } from '../a2ui/SurfaceRenderer.tsx';
import { AnswerProse } from './TextPage.tsx';
import type { LoadingLine } from './loading-line.ts';
import type { StageView } from './stage-view.ts';
import type { TurnEntry } from './turn-index.ts';
import { StagePage } from './StagePage.tsx';
import { StageCanvasTransition } from './StageCanvasTransition.tsx';

interface SurfaceIdentity {
  responseId?: string;
  lastTouchedResponseId?: string;
}

export interface StageConversationLogProps {
  turns: TurnEntry[];
  visibleTurnCount: number;
  displayedTurnIndex: number;
  view: StageView;
  loadingLine: LoadingLine | null;
  surfaceEntries: ReadonlyArray<readonly [string, SurfaceIdentity]>;
  viewingHistory: boolean;
  advisorLabel: string;
  welcomeEyebrow: string;
  starterPrompts: readonly string[];
  showStarterPrompts: boolean;
  ready: boolean;
  onSubmit: () => void;
  onStarterPrompt: (text: string) => void;
}

function viewBelongsToTurn(
  view: StageView,
  turn: TurnEntry,
  turnIndex: number,
  displayedTurnIndex: number,
  surfaceEntries: ReadonlyArray<readonly [string, SurfaceIdentity]>,
  viewingHistory: boolean,
): boolean {
  if (view.kind === 'text' || view.kind === 'process') {
    return view.turnIndex === turnIndex;
  }
  if (view.kind !== 'surface') {
    return false;
  }
  if (turn.surfaceIds.includes(view.surfaceId)) {
    return true;
  }
  const record = surfaceEntries.find(([id]) => id === view.surfaceId)?.[1];
  if (
    turn.responseId &&
    (record?.responseId === turn.responseId || record?.lastTouchedResponseId === turn.responseId)
  ) {
    return true;
  }
  return viewingHistory && turnIndex === displayedTurnIndex;
}

function historicalAnswer(turn: TurnEntry) {
  const surfaceId = turn.surfaceIds.at(-1);
  if (surfaceId && turn.surfaceSnapshot) {
    return (
      <SurfaceRenderer
        surfaceId={surfaceId}
        snapshot={turn.surfaceSnapshot}
        contentId={turn.surfaceContentId}
      />
    );
  }
  const text = turn.responseText || turn.surfaceFallback || turn.surfaceSnapshot?.fallbackMarkdown || '';
  return text ? <AnswerProse text={text} /> : null;
}

/** The conversation stays in one panel; the live turn still uses the normal progressive renderer. */
export const StageConversationLog: FC<StageConversationLogProps> = ({
  turns,
  visibleTurnCount,
  displayedTurnIndex,
  view,
  loadingLine,
  surfaceEntries,
  viewingHistory,
  advisorLabel,
  welcomeEyebrow,
  starterPrompts,
  showStarterPrompts,
  ready,
  onSubmit,
  onStarterPrompt,
}) => {
  const visibleTurns = turns.slice(0, visibleTurnCount);
  return (
    <>
      <div className="jfy-message-list" role="log" aria-label={advisorLabel}>
        {visibleTurns.map((turn, index) => {
          const liveView =
            index === visibleTurns.length - 1 &&
            viewBelongsToTurn(
              view,
              turn,
              index,
              displayedTurnIndex,
              surfaceEntries,
              viewingHistory,
            );
          const initialLoading = liveView && (view.kind === 'loading' || view.kind === 'unavailable');
          const hasHistoricalAnswer = Boolean(
            turn.surfaceSnapshot || turn.responseText || turn.surfaceFallback,
          );
          const showAnswer = liveView ? !initialLoading : hasHistoricalAnswer;
          const textFallback =
            turn.responseText || turn.surfaceFallback || turn.surfaceSnapshot?.fallbackMarkdown || '';

          return (
            <section key={turn.id} className="jfy-turn-group">
              {turn.request ? (
                <div className="jfy-message jfy-visitor-message">{turn.request}</div>
              ) : null}
              {showAnswer ? (
                <div className="jfy-advisor-message">
                  <p className="jfy-advisor-label">{advisorLabel}</p>
                  <div className="jfy-message jfy-advisor-bubble">
                    {turn.home && welcomeEyebrow ? (
                      <p className="jfy-welcome-eyebrow">{welcomeEyebrow}</p>
                    ) : null}
                    {liveView ? (
                      <StageCanvasTransition pageKey={view.key}>
                        <StagePage
                          view={view}
                          turns={turns}
                          loadingLine={loadingLine}
                          onSubmit={onSubmit}
                        />
                      </StageCanvasTransition>
                    ) : turn.surfaceSnapshot || textFallback ? (
                      historicalAnswer(turn)
                    ) : null}
                  </div>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
      {showStarterPrompts && starterPrompts.length > 0 ? (
        <div className="jfy-starter-chips" aria-label={advisorLabel}>
          {starterPrompts.map((prompt, index) => (
            <button
              key={`${index}:${prompt}`}
              type="button"
              className="jfy-starter-chip"
              disabled={!ready}
              onClick={() => onStarterPrompt(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
};
